import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { MinhaContaActions } from 'Store/MinhaConta/MinhaConta.actions';
import { IUserInfoResponse } from 'Data/interfaces/MinhaConta/IUserInfoResponse';
import schema from './updateConta.schema'
import { yupResolver } from '@hookform/resolvers/yup';
import FieldControled from 'Components/UI/FieldControled';
import Modal from 'Components/UI/Modal';
import Button from 'Components/UI/Button';
import Select from 'Components/UI/Select';
import UploadAvatar from 'Components/UI/UploadAvatar';
import { Icon } from 'Components/UI';
import toastHandler from 'Utils/toastHandler';
import { stringToPhone } from 'Utils/MaskUtils';
import { isObjectEmpty } from 'Utils/ObjectUtils';
import styles from './UpdateConta.module.scss';

interface IModal {
  visible?: boolean;
  isLoading?: boolean;
  userInfos: IUserInfoResponse;
  companyPositions: any;
  onCancel?: () => void;
}

const UpdateConta: FC<IModal> = ({
  visible,
  isLoading,
  userInfos,
  companyPositions,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('minhaConta');

  const [imageUser, setImageUser] = useState<string>(userInfos?.OriginalImageUrl);
  const [updateImageUser, setUpdateImageUser] = useState<boolean>(false);
  const [positionCompany, setPositionCompany] = useState<any>(userInfos.CompanyPosition?.Name);
  const [updatePositionCompany, setUpdatePositionCompany] = useState<boolean>(false);
  const positionsStudent = [
    "Estudante de Engenharia", 
    "Estudante de Arquitetura", 
    "Estagiário", 
    6, 
    13, 
    14
  ];

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      Nome: userInfos?.Nome,
      EmailAlternativo: userInfos?.EmailAlternativo,
      Telefone: stringToPhone(userInfos?.Telefone),
      Faculdade: userInfos?.Faculdade,
    },
    resolver: yupResolver(schema),
  });

  const onUploadImageUser = (uploadedImage: string) => {
    setImageUser(uploadedImage);
    setUpdateImageUser(true);
  };

  useEffect(() => {
    setImageUser(userInfos?.OriginalImageUrl);
    setPositionCompany(userInfos.CompanyPosition?.Name);
  }, [userInfos.CompanyPosition?.Name, userInfos?.OriginalImageUrl]);


  const setNewPositionCompany = (value: any) => {
    setPositionCompany(value);
    setUpdatePositionCompany(true);
  }

  const updateDadosConta = (data?: any) => {
    if (!positionCompany) {
      toastHandler.showError(t('updateConta.roleRequiredError'));
      return;
    }

    if (data) {
      data.Telefone = data.Telefone.replace(/\D/g, '');
      if (!updatePositionCompany) {
        data.AvatarFileName = imageUser === ''
          ? ''
          : imageUser?.substr(imageUser?.lastIndexOf("/") + 1);
        const companyPositionIndex = companyPositions.findIndex((obj: any) => {
          return obj.description === positionCompany
        });
        if (companyPositionIndex !== -1) {
          data.CompanyPositionId = companyPositions[companyPositionIndex].value;
        } else {
          data.CompanyPositionId = 0
        }
      } else {
        data.CompanyPositionId = positionCompany;
      }
      if (!data.EmailAlternativo) {
        data.EmailAlternativo = null;
      }
      dispatch(MinhaContaActions.updateDadosPessoais(data));
      setUpdatePositionCompany(false);
      setUpdateImageUser(false);
    }
  };

  const buttons = (
    <div>
      <Button height={37} onClick={onCancel}>
        <span style={{ padding: '0 20px' }}>{t('updateConta.cancelButton')}</span>
      </Button>
      <Button
        type="primary"
        height={37}
        loading={isLoading}
        disabled={isLoading || !isObjectEmpty(errors)}
        onClick={handleSubmit(updateDadosConta)}
      >
        <span style={{ padding: '0 18px' }}>{t('updateConta.updateButton')}</span>
      </Button>
    </div>
  );

  return (
    <Modal
      title={t('updateConta.modalTitle')}
      footerButtons={buttons}
      visible={visible}
      width={798}
      onCancel={onCancel}
    >
      <div className={styles['boxDetalhesConta']}>
        <div className={styles['DetalhesContaLeft']}>
          <UploadAvatar onUploadImage={onUploadImageUser}>
            {(isLoading: boolean) => (
              <div className={styles['changeImageWrapper']} style={{
                backgroundImage: imageUser
                  ? `linear-gradient(rgba(46,163,242, .6), rgba(46,163,242, .6)),
                        url('${updateImageUser ? process.env.REACT_APP_IMAGE_STORAGE_URL : ''}${imageUser}')`
                  : '',
              }}>
                {isLoading ? (
                  <Spin indicator={<LoadingOutlined color='white' style={{ fontSize: 40, color: 'white' }} />} />
                ) : (
                  <>
                    <Icon color='branco' customSize={19} icon='editarImg' />
                    <span className={styles['changeImageLabel']}>{t('updateConta.changeImage')}</span>
                  </>
                )}
              </div>
            )}
          </UploadAvatar>
        </div>
        <div className={styles['DetalhesContaRight']}>
          <div className={styles['boxData']}>
            <form>
              <FieldControled
                className={styles['fields']}
                name="Nome"
                label={t('updateConta.fieldName')}
                placeholder={t('updateConta.fieldNamePlaceholder')}
                control={control}
                trigger={trigger}
              />
              <FieldControled
                className={styles['fields']}
                name="EmailAlternativo"
                label={t('updateConta.fieldAlternativeEmail')}
                placeholder={t('updateConta.fieldAlternativeEmailPlaceholder')}
                control={control}
                trigger={trigger}
              />
              <FieldControled
                className={styles['fields']}
                name="Telefone"
                label={t('updateConta.fieldPhone')}
                maxLenght={15}
                customMask
                control={control}
                trigger={trigger}
              />
              <Select
                size="large"
                onSelect={setNewPositionCompany}
                className={styles['selectPositionCompany']}
                placeholder={t('updateConta.roleSelectPlaceholder')}
                options={companyPositions}
                defaultValue={positionCompany}
              />
              <FieldControled
                className={styles['fields']}
                style={{
                  visibility: positionsStudent.indexOf(positionCompany, 0) !== -1 ? 'visible' : 'hidden',
                  marginTop: '28px'
                }}
                name="Faculdade"
                label={t('updateConta.fieldCollege')}
                control={control}
                trigger={trigger}
              />
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UpdateConta;
